import EventBus from '../../EventsBus';
import { Field, FieldContext } from '../field';
import { VesselGlobal } from '../../forms/types/FormDefinition';
import { FieldDefinition } from '../types/FieldDefinition';

export class TanksField extends Field {
  private initialisedFields: Field[];
  private fuelTypes: VesselGlobal[];
  private tankRows: FieldDefinition[];
  private listeners: any[];

  constructor({
    fieldDef, context,
  }: {
    fieldDef: FieldDefinition,
    context: FieldContext
  }) {
    super({
      fieldDef, context,
    });
    this.initialisedFields = context.initialisedFields;
    this.fuelTypes = context.vesselGlobals.filter((global) => global.type === 'fuel');
    this.listeners = [];

    // We need to set up extra listeners so that the tanks field can calculate the ROB
    // for each fueltype based on whats entered in the fields
    const children = this.formDef.fields
      .filter((field) => this.fieldDef.children?.includes(field.id));

    this.tankRows = children
      .filter((field) => field.type !== 'hidden');

    this.tankRows.forEach((row) => {
      const rowChildren = row.children!.map((id) => this.formDef.fields.find((f) => f.id === id));
      const fuelTypeFieldDef = rowChildren.find((field) => field?.type === 'select');
      const robFieldDef = rowChildren.find((field) => field?.title === 'quantity');
      const supplyFieldDateDef = rowChildren.find((field) => field?.type === 'date');
      const volumeFieldDef = rowChildren.find((field) => field?.title === 'Observed Volume');

      // Add listeners to re-calculate ROBS
      this.listeners.push({ key: `${fuelTypeFieldDef!.id}:updated`, handler: this.calculateROBs.bind(this) });
      this.listeners.push({ key: `${robFieldDef!.id}:updated`, handler: this.calculateROBs.bind(this) });

      // Add listeners to enable disable validation on rows depending on what other
      // fields have content
      this.listeners.push({ key: `${robFieldDef!.id}:updated`, handler: () => this.updateValidation(row) });
      this.listeners.push({ key: `${supplyFieldDateDef!.id}:updated`, handler: () => this.updateValidation(row) });
      this.listeners.push({ key: `${volumeFieldDef!.id}:updated`, handler: () => this.updateValidation(row) });

      this.listeners.forEach(({ key, handler }) => EventBus.$on(key, handler));

      // When this field is initialised we need to update the validation and current ROB right away
      setTimeout(() => {
        this.updateValidation(row);
        this.calculateROBs();
      }, 200);

      // Recalculate after the date is set
      EventBus.$on('dateSet', () => {
        setTimeout(() => {
          this.updateValidation(row);
          this.calculateROBs();
        }, 200);
      });
    });
  }

  /**
   * When something changes we need to calculate the robs for each fuel type
   */
  private calculateROBs() {
    const fuelTotals: any = {};
    this.fuelTypes.forEach((type) => {
      fuelTotals[type.id] = 0;
    });

    // Loop over each tank row and add up the totals
    this.tankRows.forEach((row) => {
      const rowChildren = row.children!.map((id) => this.formDef.fields.find((f) => f.id === id));

      const fuelTypeFieldDef = rowChildren.find((field) => field?.type === 'select');
      const fuelTypefield = this.initialisedFields.find((f) => f.id === fuelTypeFieldDef!.id);

      const robFieldDef = rowChildren.find((field) => field?.title === 'quantity');
      const robField = this.initialisedFields.find((f) => f.id === robFieldDef!.id);

      if (fuelTypefield?.state.content && robField?.state.content) {
        const fuelType = this.fuelTypes.find((ft) => ft.id === fuelTypefield?.state.content)
          || this.fuelTypes.find((ft) => ft.sharedGlobalId === fuelTypefield?.state.content);
        if (fuelType) {
          fuelTotals[fuelType.id] += Number(robField?.state.content);
        }
      }
    });

    this.fuelTypes.forEach((fuelType) => {
      // Update the vessel global
      this.context.updateGlobal({
        globalId: fuelType.id,
        value: fuelTotals[fuelType.id],
      });

      // Update the shared global
      if (fuelType.sharedGlobalId) {
        this.context.updateGlobal({
          globalId: fuelType.sharedGlobalId,
          value: fuelTotals[fuelType.id],
        });
      }
    });
  }

  /**
   * When content is changed in either the Supply date, quantity or observed volumes
   * field for a row, we need to check which field have content and then activate/de-activate
   * validation for the entire row. If there is content in either of the above three fields,
   * validation will be enabled for all fields in the row, if there is not it will be disabled
   *
   * @param row {FieldDefinition} The row that content has changed for
   */
  private updateValidation(row: FieldDefinition) {
    const rowChildren = row.children!.map((id) => this.formDef.fields.find((f) => f.id === id));
    const robFieldDef = rowChildren.find((field) => field?.title === 'quantity');
    const robField = this.initialisedFields.find((f) => f.id === robFieldDef!.id);
    const supplyDateFieldDef = rowChildren.find((field) => field?.type === 'date');
    const supplyDateField = this.initialisedFields.find((f) => f.id === supplyDateFieldDef!.id);
    const volumeFieldDef = rowChildren.find((field) => field?.title === 'Observed Volume');
    const volumeField = this.initialisedFields.find((f) => f.id === volumeFieldDef!.id);

    const validationRequired = (!!robField?.state.content || !!supplyDateField?.state.content
      || !!volumeField?.state.content);

    rowChildren.forEach((fieldDef) => {
      const field = this.initialisedFields.find((f) => f.id === fieldDef!.id);
      if (field && validationRequired) {
        field.enableValidation();
      } else if (field) {
        field.disableValidation();
      }
    });
  }

  /**
   * Remove all listeners
   */
  destroy() {
    super.destroy();
    this.listeners.forEach(({ key, handler }) => {
      EventBus.$off(key, handler);
    });
  }
}
